import React, { useEffect, useState, useCallback } from 'react';
import { PieChart, Save, AlertTriangle, CheckCircle2, RotateCcw } from 'lucide-react';
import api from '../lib/api';
import { useProject } from '../context/ProjectContext';
import { formatBRL } from '../lib/format';
import MoneyInput from '../components/MoneyInput';
import { calcularPote } from '../lib/distribuicao';

export default function Distribution() {
  const { currentProject } = useProject();
  const [pots, setPots] = useState([]);
  const [dist, setDist] = useState([]);
  const [income, setIncome] = useState(0);
  const [receitaReal, setReceitaReal] = useState(null);   // { mes, valor }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!currentProject) return;
    setLoading(true);
    try {
      const pid = currentProject.id;
      const [p, d, m] = await Promise.all([
        api.get(`/projects/${pid}/pots`),
        api.get(`/projects/${pid}/monthly_distributions`),
        api.get(`/projects/${pid}/dashboard/monthly`, { params: { months: 12 } }),
      ]);
      setPots(p.data);
      setDist(d.data);
      // A página começava com receita zero, então a simulação não dizia nada
      // até alguém digitar um valor. Partir da última receita real torna a
      // tela útil já na abertura — e continua sendo só uma simulação.
      const comReceita = (m.data || []).filter((x) => (x.income || 0) > 0);
      const ultimo = comReceita[comReceita.length - 1];
      if (ultimo) {
        setReceitaReal({ mes: ultimo.month, valor: ultimo.income });
        setIncome((atual) => (atual > 0 ? atual : ultimo.income));
      }
    } finally { setLoading(false); }
  }, [currentProject]);
  useEffect(() => { load(); }, [load]);

  const getDist = (potId) => dist.find((d) => d.pot_id === potId) || { pot_id: potId, planned_percentage: 0, planned_minimum: 0, planned_fixed: 0 };
  const updateField = (potId, field, value) => {
    const existing = dist.find((d) => d.pot_id === potId);
    if (existing) {
      setDist(dist.map((d) => d.pot_id === potId ? { ...d, [field]: parseFloat(value) || 0 } : d));
    } else {
      setDist([...dist, { pot_id: potId, planned_percentage: 0, planned_minimum: 0, planned_fixed: 0, [field]: parseFloat(value) || 0 }]);
    }
  };

  const totalPercent = dist.reduce((s, d) => s + (d.planned_percentage || 0), 0);
  const totalFixed = dist.reduce((s, d) => s + (d.planned_fixed || 0), 0);

  // Resumo: o que a distribuição de fato compromete da receita simulada.
  const resumo = (() => {
    const comRegra = pots.filter((p) => {
      const d = dist.find((x) => x.pot_id === p.id);
      return d && (d.planned_percentage || d.planned_minimum || d.planned_fixed);
    });
    const distribuido = comRegra.reduce(
      (s, p) => s + calcularPote(dist.find((x) => x.pot_id === p.id), income), 0);
    const porMinimo = comRegra.filter((p) => {
      const d = dist.find((x) => x.pot_id === p.id);
      return (d.planned_minimum || 0) > ((d.planned_percentage || 0) / 100) * income;
    }).length;
    return {
      distribuido,
      sobra: income - distribuido,
      comRegra: comRegra.length,
      semRegra: pots.length - comRegra.length,
      porMinimo,
      proporcao: income > 0 ? (distribuido / income) * 100 : 0,
    };
  })();

  const saveAll = async () => {
    setSaving(true);
    try {
      for (const d of dist) {
        if (d.id) {
          await api.put(`/monthly_distributions/${d.id}`, d);
        } else if (d.planned_percentage || d.planned_minimum || d.planned_fixed) {
          await api.post(`/projects/${currentProject.id}/monthly_distributions`, d);
        }
      }
      await load();
    } finally { setSaving(false); }
  };

  if (!currentProject) return <div className="text-slate-500 dark:text-slate-400">Selecione um projeto.</div>;
  if (loading) return <div className="text-slate-500 dark:text-slate-400">Carregando...</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2"><PieChart className="w-7 h-7 text-indigo-600" /> Distribuição Mensal</h2>
          <p className="text-slate-500 dark:text-slate-400">Planeje como sua receita será distribuída pelos potes</p>
        </div>
        <button onClick={saveAll} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white rounded-lg shadow-md disabled:opacity-50">
          <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar Tudo'}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-5">
          <div className="text-sm text-slate-500 dark:text-slate-400">Receita do Mês (simulada)</div>
          <MoneyInput value={income} onChange={(v) => setIncome(v)} data-testid="dist-income"
            className="w-full mt-2 text-2xl font-bold text-indigo-600 border-b-2 border-slate-200 dark:border-slate-700 focus:border-indigo-500 outline-none py-1 bg-transparent" />
          {receitaReal && (
            <button type="button" onClick={() => setIncome(receitaReal.valor)}
              className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400 hover:text-indigo-600">
              <RotateCcw className="w-3 h-3" />
              usar a receita real de {receitaReal.mes} ({formatBRL(receitaReal.valor)})
            </button>
          )}
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-5">
          <div className="text-sm text-slate-500 dark:text-slate-400">Total Percentual</div>
          <div className={`text-2xl font-bold mt-2 ${totalPercent > 100 ? 'text-rose-600' : totalPercent === 100 ? 'text-emerald-600' : 'text-slate-700 dark:text-slate-300'}`}>{totalPercent.toFixed(1)}%</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{totalPercent > 100 ? 'Excedeu 100%' : totalPercent < 100 ? `Faltam ${(100 - totalPercent).toFixed(1)}%` : 'Distribuído'}</div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm p-5">
          <div className="text-sm text-slate-500 dark:text-slate-400">Valores Fixos</div>
          <div className="text-2xl font-bold mt-2 text-blue-600">{formatBRL(totalFixed)}</div>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm overflow-hidden">
        {/* No celular a tabela obrigava a rolar de lado PARA DIGITAR — e esta
            é uma tela de digitar. Cada pote vira um cartão com os três campos
            lado a lado, que cabem em 375px. */}
        <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-700">
          {pots.map((p) => {
            const d = getDist(p.id);
            const computed = calcularPote(d, income);
            return (
              <div key={p.id} className="p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ background: p.color || '#6366f1' }}>{(p.name||'?')[0]}</div>
                    <div className="min-w-0">
                      <div className="font-medium text-slate-800 dark:text-slate-200 truncate">{p.name}</div>
                      {p.type && <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{p.type}</div>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-slate-400">Simulação</div>
                    <div className="font-semibold text-indigo-600 whitespace-nowrap">{formatBRL(computed)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ['% Receita', 'planned_percentage', '0'],
                    ['Mínimo', 'planned_minimum', '0,00'],
                    ['Fixo', 'planned_fixed', '0,00'],
                  ].map(([rotulo, campo, dica]) => (
                    <div key={campo}>
                      <label className="block text-[11px] text-slate-500 dark:text-slate-400 mb-1">{rotulo}</label>
                      <MoneyInput value={d[campo] || 0} onChange={(v) => updateField(p.id, campo, v)}
                        className="w-full px-2 py-2 text-sm border border-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 rounded"
                        placeholder={dica} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700 text-left text-slate-600 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3">Pote</th>
                <th className="px-4 py-3 w-32">% Receita</th>
                <th className="px-4 py-3 w-32">Mínimo (R$)</th>
                <th className="px-4 py-3 w-32">Fixo (R$)</th>
                <th className="px-4 py-3 text-right">Simulação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {pots.map((p) => {
                const d = getDist(p.id);
                const computed = calcularPote(d, income);
                return (
                  <tr key={p.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-xs" style={{ background: p.color || '#6366f1' }}>{(p.name||'?')[0]}</div>
                        <div>
                          <div className="font-medium text-slate-800 dark:text-slate-200">{p.name}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400">{p.type || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><MoneyInput value={d.planned_percentage || 0} onChange={(v) => updateField(p.id, 'planned_percentage', v)} className="w-24 px-2 py-1 border border-slate-200 dark:border-slate-700 rounded" placeholder="0" /></td>
                    <td className="px-4 py-3"><MoneyInput value={d.planned_minimum || 0} onChange={(v) => updateField(p.id, 'planned_minimum', v)} className="w-24 px-2 py-1 border border-slate-200 dark:border-slate-700 rounded" placeholder="0,00" /></td>
                    <td className="px-4 py-3"><MoneyInput value={d.planned_fixed || 0} onChange={(v) => updateField(p.id, 'planned_fixed', v)} className="w-24 px-2 py-1 border border-slate-200 dark:border-slate-700 rounded" placeholder="0,00" /></td>
                    <td className="px-4 py-3 text-right font-semibold text-indigo-600 whitespace-nowrap">{formatBRL(computed)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ResumoDistribuicao resumo={resumo} income={income} totalPercent={totalPercent} totalFixed={totalFixed} />
    </div>
  );
}

/**
 * Fecha a conta da distribuição: quanto dos R$ simulados a regra compromete,
 * quanto sobra, e o que precisa de atenção.
 *
 * O "sobra" é o número que faltava na tela: dá para ter 100% em percentuais e
 * ainda assim estourar a receita, porque valores fixos e mínimos entram por
 * fora do percentual.
 */
function ResumoDistribuicao({ resumo, income, totalPercent, totalFixed }) {
  const estourou = resumo.sobra < -0.005;
  const exato = Math.abs(resumo.sobra) <= 0.005;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/70 dark:border-slate-700 shadow-sm overflow-hidden" data-testid="dist-resumo">
      <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Resumo da distribuição</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">Sobre a receita simulada de {formatBRL(income)}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-slate-100 dark:divide-slate-700 border-b border-slate-100 dark:border-slate-700">
        <Bloco titulo="Total distribuído" valor={formatBRL(resumo.distribuido)}
          detalhe={income > 0 ? `${resumo.proporcao.toFixed(1)}% da receita` : 'informe a receita'}
          cor="text-indigo-600" testid="dist-total" />
        <Bloco titulo={estourou ? 'Faltam' : 'Sobra livre'}
          valor={formatBRL(Math.abs(resumo.sobra))}
          detalhe={estourou ? 'a distribuição excede a receita' : exato ? 'distribuição exata' : 'ainda não destinado'}
          cor={estourou ? 'text-rose-600' : exato ? 'text-emerald-600' : 'text-slate-800 dark:text-slate-200'} testid="dist-sobra" />
        <Bloco titulo="Em percentual" valor={`${totalPercent.toFixed(1)}%`}
          detalhe={formatBRL((totalPercent / 100) * income)} cor="text-slate-800 dark:text-slate-200" />
        <Bloco titulo="Em valor fixo" valor={formatBRL(totalFixed)}
          detalhe="somado por fora do percentual" cor="text-slate-800 dark:text-slate-200" />
      </div>

      <div className="px-6 py-4 space-y-2 text-sm">
        {estourou && (
          <Aviso tom="erro" Icone={AlertTriangle}>
            A distribuição compromete <strong>{formatBRL(resumo.distribuido)}</strong> de uma receita
            de {formatBRL(income)} — <strong>{formatBRL(Math.abs(resumo.sobra))} a mais do que entra</strong>.
            Valores fixos e mínimos entram por fora do percentual, então dá para estourar mesmo com
            os percentuais somando menos de 100%.
          </Aviso>
        )}
        {exato && income > 0 && (
          <Aviso tom="ok" Icone={CheckCircle2}>
            A receita está inteiramente destinada, sem sobra e sem excesso.
          </Aviso>
        )}
        {resumo.semRegra > 0 && (
          <Aviso tom="neutro" Icone={AlertTriangle}>
            <strong>{resumo.semRegra}</strong> {resumo.semRegra === 1 ? 'pote não recebe' : 'potes não recebem'} nada
            nesta distribuição. Isso é normal para potes de cartão e de acertos, que se alimentam de outros lançamentos.
          </Aviso>
        )}
        {resumo.porMinimo > 0 && (
          <Aviso tom="neutro" Icone={AlertTriangle}>
            Em <strong>{resumo.porMinimo}</strong> {resumo.porMinimo === 1 ? 'pote o mínimo está valendo' : 'potes o mínimo está valendo'} mais
            que o percentual. Se a receita subir, o percentual passa a mandar e o valor muda sozinho.
          </Aviso>
        )}
        {!estourou && !exato && income > 0 && resumo.sobra > 0 && (
          <Aviso tom="neutro" Icone={AlertTriangle}>
            Sobram <strong>{formatBRL(resumo.sobra)}</strong> sem destino definido. Eles ficam no
            Pote Distribuição até você mandar para algum lugar.
          </Aviso>
        )}
      </div>
    </div>
  );
}

function Bloco({ titulo, valor, detalhe, cor, testid }) {
  return (
    <div className="px-6 py-4" data-testid={testid}>
      <div className="text-xs text-slate-500 dark:text-slate-400">{titulo}</div>
      <div className={`text-xl md:text-2xl font-bold mt-1 ${cor}`}>{valor}</div>
      <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{detalhe}</div>
    </div>
  );
}

function Aviso({ tom, Icone, children }) {
  const cores = {
    erro: 'bg-rose-50 border-rose-200 text-rose-800',
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    neutro: 'bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400',
  }[tom];
  return (
    <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${cores}`}>
      <Icone className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="leading-snug">{children}</div>
    </div>
  );
}
